import React, { useCallback, useRef, useState } from 'react';
import { Badge } from './ui/badge';
import {
  Award, CheckCircle2, Clock, AlertTriangle, XCircle, FileText, ExternalLink, Loader2,
  Check, X, RotateCcw,
} from 'lucide-react';
import api from '../utils/api';
import { formatDate } from '../utils/timeFormat';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

/**
 * Iter-202 — Reusable certification display.
 * Iter-227n — adds inline admin actions: Approve / Reject / Request resubmission.
 *
 * Source of truth: `talent_certifications`. The badge always reflects the
 * server `effective_status` (approved→expired flip is computed serverside).
 *
 * Props:
 *   certifications: array of cert objects from backend
 *   variant: 'full' (default) | 'compact' (chip-only) | 'inline' (max 3)
 *   showDocLink: bool — whether to render the "View document" link
 *   emptyMessage: override the "No certifications uploaded yet." copy
 *   testIdPrefix: optional prefix for data-testid
 *   adminMode: bool — when true, pending/rejected certs render
 *               Approve / Reject / Resubmit buttons inline (Iter-227n)
 *   onReviewed: function(certId, newStatus) — called after a successful
 *               review so the parent can refresh the list / counts
 *
 * Each cert is expected to have:
 *   { id, certification_type, type_name, status, effective_status,
 *     expiration_date, is_expired, is_expiring_soon,
 *     days_until_expiration, document_url?, rejection_reason? }
 */

const STATUS_META = {
  approved: {
    label: 'Approved',
    icon: CheckCircle2,
    className: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  },
  pending: {
    label: 'Pending Review',
    icon: Clock,
    className: 'bg-amber-50 text-amber-700 border border-amber-200',
  },
  expired: {
    label: 'Expired',
    icon: AlertTriangle,
    className: 'bg-rose-50 text-rose-700 border border-rose-200',
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    className: 'bg-gray-100 text-gray-600 border border-gray-200',
  },
};

const formatExpiration = (cert) => {
  if (!cert?.expiration_date) return null;
  const formatted = formatDate(cert.expiration_date);
  if (cert.is_expired) {
    const daysAgo = Math.abs(Number(cert.days_until_expiration) || 0);
    return { text: `Expired ${formatted}${daysAgo > 0 ? ` · ${daysAgo}d ago` : ''}`, tone: 'rose' };
  }
  if (cert.is_expiring_soon) {
    const days = Number(cert.days_until_expiration) || 0;
    return { text: `Expires ${formatted} · in ${days}d`, tone: 'amber' };
  }
  return { text: `Expires ${formatted}`, tone: 'gray' };
};

const StatusBadge = ({ status, testid }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <Badge
      className={`${meta.className} inline-flex items-center gap-1 text-[10px] uppercase tracking-wide`}
      data-testid={testid}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </Badge>
  );
};

// Iter-227n — Reject / Resubmit reason dialog. Loaded options come from
// the canonical /api/certifications/admin/rejection-reasons endpoint
// but fall back to a static list if the request fails.
const FALLBACK_REASONS = [
  'Photo too blurry — text not legible',
  'Document is expired',
  'Wrong document type uploaded',
  'Name on document does not match account',
  'Missing required pages',
  'Other (see notes)',
];

const ReviewActionDialog = ({ open, onOpenChange, mode, cert, onSubmit, busy }) => {
  const [reason, setReason] = useState(FALLBACK_REASONS[0]);
  const [notes, setNotes] = useState('');
  const [reasonOptions, setReasonOptions] = useState(FALLBACK_REASONS);

  React.useEffect(() => {
    if (!open) return;
    setReason(FALLBACK_REASONS[0]);
    setNotes('');
    (async () => {
      try {
        const res = await api.get('/certifications/admin/rejection-reasons');
        const items = Array.isArray(res.data) ? res.data
          : Array.isArray(res.data?.reasons) ? res.data.reasons
            : null;
        if (items && items.length) setReasonOptions(items);
      } catch (_e) {
        // keep FALLBACK_REASONS
      }
    })();
  }, [open]);

  const isReject = mode === 'reject';
  const title = isReject ? 'Reject certification' : 'Request resubmission';
  const cta = isReject ? 'Reject certification' : 'Request resubmission';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cert-review-action-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {cert?.type_name || cert?.certification_type} for this talent
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Reason</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="cert-review-reason-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {reasonOptions.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">
              Notes <span className="text-gray-400">(optional, shared with talent)</span>
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="e.g. Please hold the ID closer to the camera."
              data-testid="cert-review-notes-textarea"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit({ reason, notes })}
            disabled={busy || !reason}
            className={isReject ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'}
            data-testid="cert-review-action-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CertificationList = ({
  certifications,
  variant = 'full',
  showDocLink = false,
  emptyMessage = 'No certifications uploaded yet.',
  testIdPrefix = 'cert',
  adminMode = false,
  onReviewed,
}) => {
  const list = Array.isArray(certifications) ? certifications.filter(Boolean) : [];

  // Iter-203 — fetch document via auth-aware client → blob → window.open.
  const blobCacheRef = useRef(new Map());
  const openingRef = useRef(new Set());

  // Iter-227n — inline admin action state
  const [busyCertId, setBusyCertId] = useState(null);
  const [dialog, setDialog] = useState({ open: false, mode: null, cert: null });

  const openDocument = useCallback(async (docUrl, _certKey) => {
    if (!docUrl) return;
    const cached = blobCacheRef.current.get(docUrl);
    if (cached) {
      window.open(cached, '_blank', 'noopener,noreferrer');
      return;
    }
    if (openingRef.current.has(docUrl)) return;
    openingRef.current.add(docUrl);
    try {
      const apiPath = docUrl.startsWith('/api/') ? docUrl.slice(4) : docUrl;
      const res = await api.get(apiPath, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      blobCacheRef.current.set(docUrl, url);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) toast.error('You do not have permission to view this document.');
      else if (status === 404) toast.error('Document not found.');
      else toast.error('Could not load document — please try again.');
    } finally {
      openingRef.current.delete(docUrl);
    }
  }, []);

  // Iter-227n — inline approve action
  const handleApprove = useCallback(async (cert) => {
    if (!cert?.id) return;
    if (busyCertId) return;
    setBusyCertId(cert.id);
    try {
      await api.put(`/certifications/admin/${cert.id}/review`, { status: 'approved' });
      toast.success(`${cert.type_name || cert.certification_type} approved`);
      onReviewed?.(cert.id, 'approved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to approve certification');
    } finally {
      setBusyCertId(null);
    }
  }, [busyCertId, onReviewed]);

  // Iter-227n — inline reject / resubmit via dialog
  const handleDialogSubmit = useCallback(async ({ reason, notes }) => {
    const { mode, cert } = dialog;
    if (!cert?.id || !mode) return;
    setBusyCertId(cert.id);
    try {
      if (mode === 'reject') {
        await api.put(`/certifications/admin/${cert.id}/review`, {
          status: 'rejected',
          rejection_reason: reason,
          notes,
        });
        toast.success('Certification rejected');
        onReviewed?.(cert.id, 'rejected');
      } else if (mode === 'resubmit') {
        await api.post(`/certifications/admin/${cert.id}/request-resubmission`, {
          reason,
          notes,
        });
        toast.success('Resubmission requested — talent has been notified');
        onReviewed?.(cert.id, 'rejected');
      }
      setDialog({ open: false, mode: null, cert: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update certification');
    } finally {
      setBusyCertId(null);
    }
  }, [dialog, onReviewed]);

  if (list.length === 0) {
    return (
      <div
        className="text-xs text-gray-500 inline-flex items-center gap-1.5"
        data-testid={`${testIdPrefix}-empty`}
      >
        <FileText className="w-3.5 h-3.5 text-gray-400" />
        {emptyMessage}
      </div>
    );
  }

  // Iter-227n — admin action buttons (shared between inline + full variants)
  const renderAdminActions = (c, sizeCls = 'text-[10px] px-1.5 py-0.5') => {
    if (!adminMode) return null;
    const status = c.effective_status || c.status || 'pending';
    // Approved + non-expired: nothing to do.
    if (status === 'approved') return null;
    const isBusy = busyCertId === c.id;
    return (
      <span className="inline-flex items-center gap-1" data-testid={`${testIdPrefix}-admin-actions-${c.certification_type}`}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleApprove(c); }}
          disabled={isBusy}
          className={`${sizeCls} inline-flex items-center gap-1 rounded border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50`}
          data-testid={`${testIdPrefix}-approve-${c.certification_type}`}
          title="Approve certification"
        >
          {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setDialog({ open: true, mode: 'reject', cert: c }); }}
          disabled={isBusy}
          className={`${sizeCls} inline-flex items-center gap-1 rounded border border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100 disabled:opacity-50`}
          data-testid={`${testIdPrefix}-reject-${c.certification_type}`}
          title="Reject certification"
        >
          <X className="w-3 h-3" /> Reject
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setDialog({ open: true, mode: 'resubmit', cert: c }); }}
          disabled={isBusy}
          className={`${sizeCls} inline-flex items-center gap-1 rounded border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 disabled:opacity-50`}
          data-testid={`${testIdPrefix}-resubmit-${c.certification_type}`}
          title="Request resubmission"
        >
          <RotateCcw className="w-3 h-3" /> Resubmit
        </button>
      </span>
    );
  };

  const dialogNode = (
    <ReviewActionDialog
      open={dialog.open}
      onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
      mode={dialog.mode}
      cert={dialog.cert}
      onSubmit={handleDialogSubmit}
      busy={!!busyCertId}
    />
  );

  // Compact pills — used on Discover cards. Status-color, max 4. (no admin actions)
  if (variant === 'compact') {
    const items = list.slice(0, 4);
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-compact`}>
        {items.map((c) => {
          const status = c.effective_status || c.status || 'pending';
          const meta = STATUS_META[status] || STATUS_META.pending;
          const Icon = meta.icon;
          return (
            <span
              key={c.id || c.certification_type}
              className={`${meta.className} inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full`}
              title={`${c.type_name || c.certification_type} · ${meta.label}`}
              data-testid={`${testIdPrefix}-pill-${c.certification_type}`}
            >
              <Icon className="w-3 h-3" />
              {c.type_name || c.certification_type}
            </span>
          );
        })}
        {list.length > items.length && (
          <span className="text-[10px] text-gray-500 px-1.5 py-0.5">
            +{list.length - items.length} more
          </span>
        )}
      </div>
    );
  }

  // Inline — Applicant Drawer / cards (full info but space-tight). Admin actions render below the row.
  if (variant === 'inline') {
    const items = list.slice(0, 3);
    return (
      <>
        <ul className="space-y-1" data-testid={`${testIdPrefix}-inline`}>
          {items.map((c) => {
            const status = c.effective_status || c.status || 'pending';
            const exp = formatExpiration(c);
            const docUrl = showDocLink ? c.document_url : null;
            return (
              <li
                key={c.id || c.certification_type}
                className="flex flex-col gap-1 text-xs"
                data-testid={`${testIdPrefix}-row-${c.certification_type}`}
              >
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Award className="w-3 h-3 text-amber-600 shrink-0" />
                  <span className="font-medium text-gray-800">
                    {c.type_name || c.certification_type}
                  </span>
                  <StatusBadge status={status} testid={`${testIdPrefix}-status-${c.certification_type}`} />
                  {exp && (
                    <span
                      className={
                        exp.tone === 'rose' ? 'text-rose-600'
                          : exp.tone === 'amber' ? 'text-amber-600'
                            : 'text-gray-500'
                      }
                    >
                      · {exp.text}
                    </span>
                  )}
                  {docUrl && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openDocument(docUrl, c.certification_type); }}
                      className="inline-flex items-center gap-1 text-[11px] text-cyan-700 hover:text-cyan-800 hover:underline"
                      data-testid={`${testIdPrefix}-doc-link-${c.certification_type}`}
                      title="Open document in a new tab"
                    >
                      <ExternalLink className="w-3 h-3" /> View
                    </button>
                  )}
                </div>
                {renderAdminActions(c)}
              </li>
            );
          })}
          {list.length > items.length && (
            <li className="text-[11px] text-gray-500 italic">
              +{list.length - items.length} more
            </li>
          )}
        </ul>
        {dialogNode}
      </>
    );
  }

  // Full — used in profile modal / detail pages.
  return (
    <>
      <ul className="space-y-2" data-testid={`${testIdPrefix}-list`}>
        {list.map((c) => {
          const status = c.effective_status || c.status || 'pending';
          const exp = formatExpiration(c);
          const docUrl = showDocLink ? c.document_url : null;
          return (
            <li
              key={c.id || c.certification_type}
              className="rounded-lg border border-gray-200 bg-white p-3"
              data-testid={`${testIdPrefix}-card-${c.certification_type}`}
            >
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="flex items-start gap-2 min-w-0">
                  <Award className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-gray-900 truncate">
                      {c.type_name || c.certification_type}
                    </div>
                    {exp && (
                      <div
                        className={`text-xs mt-0.5 ${
                          exp.tone === 'rose' ? 'text-rose-600'
                            : exp.tone === 'amber' ? 'text-amber-600'
                              : 'text-gray-500'
                        }`}
                        data-testid={`${testIdPrefix}-expiration-${c.certification_type}`}
                      >
                        {exp.text}
                      </div>
                    )}
                    {status === 'rejected' && c.rejection_reason && (
                      <div className="text-xs text-gray-600 mt-0.5 italic">
                        {c.rejection_reason}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                  <StatusBadge status={status} testid={`${testIdPrefix}-status-${c.certification_type}`} />
                  {docUrl && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openDocument(docUrl, c.certification_type); }}
                      className="inline-flex items-center gap-1 text-[11px] text-cyan-700 hover:text-cyan-800 hover:underline"
                      data-testid={`${testIdPrefix}-doc-link-${c.certification_type}`}
                      title="Open document in a new tab"
                    >
                      <ExternalLink className="w-3 h-3" /> View
                    </button>
                  )}
                </div>
              </div>
              {/* Iter-227n — admin action row */}
              {adminMode && (status === 'pending' || status === 'rejected') && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  {renderAdminActions(c, 'text-[11px] px-2 py-1')}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {dialogNode}
    </>
  );
};

export default CertificationList;
